/**
 * Section list, ordering, and display labels for the applicant-web
 * intake.
 *
 * The catalog (`@inside-us-aos/domain`) is the
 * source of truth for which sections exist — this module enumerates
 * them by reading every `FieldDefinition.sectionId` and deduplicating.
 *
 * Two app-level concerns layered on top:
 *
 * 1. **Display labels.** A curated map (`SECTION_DISPLAY_LABELS`)
 *    converts machine `sectionId` values into human-readable titles.
 *    Sections without a curated label fall back to a title-cased
 *    version of the underscore-separated id.
 * 2. **Ordering.** `ORDERED_SECTIONS` is an explicit array that
 *    the sidebar walks top-down. The default ordering follows USCIS
 *    form order (identity → biographic → … → signatures).
 *
 * The plan (`docs/rebuild/phase-7-plan.md`) flagged section ordering
 * and display labels as app-side concerns, not catalog-side. Both
 * live here so the catalog stays a pure data package.
 */
import {
  aosV1FieldDefinitions,
  type FieldDefinition,
} from '@inside-us-aos/domain';

/**
 * Set of distinct catalog `sectionId` values, derived at module load.
 * Order is insertion-order from the catalog and not used for
 * presentation — `ORDERED_SECTIONS` is the canonical render order.
 */
export const SECTION_IDS: readonly string[] = (() => {
  const seen = new Set<string>();
  for (const field of aosV1FieldDefinitions) {
    seen.add(field.sectionId);
  }
  return Array.from(seen);
})();

/**
 * Phase 7i — module-level index from `sectionId` → catalog fields.
 * Replaces `aosV1FieldDefinitions.filter(f => f.sectionId === sid)`
 * (an O(N=580) scan) with an O(1) Map lookup. The catalog is frozen
 * data so this index is built once at module load and shared by every
 * caller. Lookups returning `undefined` are equivalent to "no fields"
 * — callers should treat the empty case identically to a section with
 * zero hits, which matches the prior `.filter` semantics.
 */
export const FIELDS_BY_SECTION_ID: ReadonlyMap<
  string,
  readonly FieldDefinition[]
> = (() => {
  const map = new Map<string, FieldDefinition[]>();
  for (const field of aosV1FieldDefinitions) {
    const existing = map.get(field.sectionId);
    if (existing === undefined) {
      map.set(field.sectionId, [field]);
    } else {
      existing.push(field);
    }
  }
  return map;
})();

/**
 * Returns the catalog fields belonging to a section, or an empty array
 * if the section is unknown. Used by SectionRenderer, App, and the
 * review panel to avoid repeated O(N) catalog scans on every render.
 */
export function getFieldsForSection(
  sectionId: string,
): readonly FieldDefinition[] {
  return FIELDS_BY_SECTION_ID.get(sectionId) ?? EMPTY_FIELDS;
}
const EMPTY_FIELDS: readonly FieldDefinition[] = [];

/**
 * Human-readable display labels for catalog sections. Sections not in
 * this map fall back to a title-cased version of the id (see
 * `getSectionDisplayLabel`).
 */
export const SECTION_DISPLAY_LABELS: Readonly<Record<string, string>> = {
  identity: 'Personal info',
  physical_address: 'Where you live',
  biographic: 'Biographic Information',
  marriage: 'Marriage History',
  children: 'Children',
  parents: 'Parents',
  immigration_history: 'Immigration History',
  travel_history: 'Travel History',
  travel_document: 'Travel Document',
  employment: 'Employment History',
  eligibility: 'Eligibility',
  eligibility_category: 'Eligibility Category',
  documents: 'Supporting Documents',
  signatures: 'Signatures',
  sponsor_identity: 'Sponsor personal info',
  sponsor_physical_address: 'Where your sponsor lives',
  sponsor_marriage: 'Sponsor Marriage History',
  sponsor_parents: 'Sponsor Parents',
  sponsor_household: 'Sponsor Household',
  financials: 'Sponsor Financials',
  sponsor_basis_for_filing: 'Sponsor Basis for Filing',
  accommodation: 'Accommodation Requests',
  additional_info: 'Additional Information',
  petition_type: 'Petition Type',
};

/**
 * Returns the curated display label for a section, falling back to a
 * title-cased version of the underscore-separated id. The fallback
 * is used both for unknown sections and for catalog additions the
 * curator hasn't yet labeled.
 */
export function getSectionDisplayLabel(sectionId: string): string {
  const curated = SECTION_DISPLAY_LABELS[sectionId];
  if (curated !== undefined) return curated;
  return sectionId
    .split('_')
    .map(part => (part.length === 0 ? part : part[0].toUpperCase() + part.slice(1)))
    .join(' ');
}

/**
 * One-line section intros.
 *
 * Authored conservatively: only the sections most prone to "didn't I
 * just answer this?" deja vu carry an intro string. Sections whose
 * title is self-explanatory (`identity`, `documents`, `signatures`)
 * deliberately have no entry here so the renderer leaves them quiet.
 *
 * Authoring rules (from `phase-7ia-plan.md`, Slice 6):
 *   1. Plain second-person English. Bold key tokens with Markdown-
 *      style `**` if helpful — the renderer treats the string as
 *      plain text today; future polish can parse it.
 *   2. Name *whose* info, *why* USCIS asks, and what makes this
 *      section different from a similar one.
 *   3. ~140 chars max so the intro doesn't push the first question
 *      off-screen.
 *
 * The `**` markers are kept verbatim in the string for now — the UI
 * renders them as literal characters rather than parsing Markdown.
 * That's fine: the emphasis still reads, and a Phase 7c polish pass
 * can add a tiny inline-bold renderer without changing the data.
 */
export const SECTION_INTROS: Readonly<Record<string, string>> = {
  physical_address:
    "Where you currently live and any address you've had over the past 5 years, including the last address you had outside the United States.",
  marriage:
    'Your marriage history. List your **current** marriage first, then any prior marriages. The dates tell USCIS which ones to verify.',
  parents:
    "Your **biological or adoptive** parents. USCIS asks about both even when one is deceased or out of contact.",
  children:
    "List **all** of your children — biological, adopted, or stepchildren — even if they don't live with you and aren't applying with you.",
  immigration_history:
    "Every visa, status, and entry into the U.S. that's on record. If you can't remember a date, the closest month/year is fine.",
  travel_history:
    "Trips you've taken **outside** the U.S. since your last entry. Trips before that are usually not required unless we ask about them.",
  employment:
    'Your work history for the past **5 years**. Include unemployment periods or gaps in school as separate entries.',
  sponsor_identity:
    'Details about your **sponsor** — the U.S. citizen or permanent resident who filed the I-130 petition for you. Their answers are separate from yours.',
  sponsor_physical_address:
    "Where your sponsor currently lives, plus any address they've had over the past 5 years. USCIS uses this to verify the sponsor's domicile.",
  sponsor_marriage:
    "Your **sponsor's** marriage history. List the current marriage (to you) first, then any prior marriages they've had.",
  sponsor_household:
    "Everyone living in your sponsor's household — this affects the income threshold for the I-864 affidavit of support.",
  financials:
    "Your sponsor's income and assets. The I-864 needs at least 125% of the federal poverty guideline for the household size.",
  eligibility:
    'A short series of yes/no questions. Each is worded precisely — read carefully and answer based on your own situation.',
};

/**
 * Returns the curated one-line intro for a section, or `undefined` if
 * the section has no authored intro. Sparse on purpose: SectionRenderer
 * renders nothing when the result is `undefined`.
 */
export function getSectionIntro(sectionId: string): string | undefined {
  return SECTION_INTROS[sectionId];
}

/*
 * "Why USCIS asks" copy.
 *
 * Section intros tell the applicant *what* to fill in. The "why" copy
 * tells them *why USCIS cares*, expanded inside a `<details>` element
 * so the page stays scannable. We seed the most-emotionally-loaded
 * sections — marriage, parents, immigration history, travel, sponsor
 * household, financials — because those are the ones where applicants
 * most often worry that an answer will harm their case.
 *
 * Sparse on purpose. Adding a why for a new section is a one-line
 * insertion; sections without a why simply don't render the
 * collapsible.
 *
 * Tone guidelines:
 *   1. State the USCIS reason in one sentence. No legal advice.
 *   2. If there's a common worry, name it and reassure (e.g., a prior
 *      marriage doesn't disqualify you; deceased parents still get
 *      listed).
 *   3. Keep it short — this is a tooltip, not an essay.
 */
export const SECTION_WHY: Readonly<Record<string, string>> = {
  identity:
    "Your basic identity facts — legal name, date of birth, place of birth. USCIS matches these against existing records. Use the spelling exactly as it appears on official documents (passport, prior USCIS notices); inconsistencies are the most common reason cases get returned.",
  physical_address:
    "Where you live now and every U.S. address you've had over the past 5 years. USCIS uses this to verify continuous presence and to know where to send your green card. Best-effort dates are fine if you can't recall exact move-in days.",
  biographic:
    "Height, weight, hair and eye color, ethnicity, race, gender — used for your biometric record. Estimates are fine; you'll be measured at the biometrics appointment anyway.",
  marriage:
    'USCIS reviews marriage history to verify the legitimacy of your current marriage and confirm prior marriages were properly ended. A prior divorce does not weaken your case — incomplete records do.',
  parents:
    "List parents even if they're deceased, estranged, or unknown. USCIS uses these entries for identity verification, not eligibility. Missing fields slow processing more than honest 'unknown' answers.",
  children:
    'List every biological, adopted, and stepchild — including those not joining the application. Omitting a child is one of the most common reasons cases get returned for evidence.',
  immigration_history:
    "Every entry, visa, and status change USCIS already has on record. They're checking for consistency, not catching mistakes — best-effort dates beat blank fields.",
  travel_history:
    'Trips outside the U.S. since your last entry. USCIS uses this to confirm you maintained continuous presence. Short border crossings count; vacations under 24 hours can usually be summarized.',
  travel_document:
    "Your passport (or other travel document) details. USCIS uses this to confirm your identity at port of entry and on the green card itself. Even if your passport has expired since you arrived, USCIS wants the most recent one.",
  employment:
    "Work history for the past 5 years. Include unemployment periods or schooling as separate entries — gaps don't disqualify you, they just need to be accounted for so USCIS sees a continuous timeline.",
  eligibility:
    'These yes/no questions screen for inadmissibility grounds. Most applicants answer "no" to all of them; honest answers are required even when a "yes" might require additional documentation.',
  eligibility_category:
    "The category of green card you're applying for. Marriage to a U.S. citizen is the most common path; the screener narrowed this down for you. If unsure, your case team confirms it during review.",
  sponsor_identity:
    "Your sponsor — the U.S. citizen or permanent resident filing the I-130 petition for you. USCIS uses their info to confirm the qualifying relationship. The sponsor's answers stay separate from yours throughout the application.",
  sponsor_physical_address:
    "Where your sponsor currently lives, plus any address they've had over the past 5 years. USCIS uses this to verify the sponsor's domicile (an I-864 requirement) and to send case correspondence.",
  sponsor_marriage:
    "Your sponsor's marriage history. Required even if their only marriage is the one to you — USCIS uses this to confirm any prior marriages were properly ended before yours began.",
  sponsor_basis_for_filing:
    "How your sponsor qualifies — through marriage to you, in this case. USCIS uses this section to confirm the qualifying relationship is the one driving the petition.",
  sponsor_household:
    "The household size determines the income threshold for the I-864 affidavit of support. Counting everyone the sponsor financially supports (not just people who live there) is what USCIS expects.",
  financials:
    'The I-864 needs the sponsor to show income at or above 125% of the federal poverty guideline for the household size. If income is short, assets and joint sponsors can fill the gap.',
  petition_type:
    "Confirms the immigrant petition you're applying under (I-130 spouse petition for marriage cases). USCIS uses this to route the case correctly and pair it with the petition the sponsor filed for you.",
};

/**
 * Returns the curated "why USCIS asks" expansion for a section, or
 * `undefined` when no why has been authored. SectionRenderer uses this
 * to decide whether to render the collapsible block under the intro.
 */
export function getSectionWhy(sectionId: string): string | undefined {
  return SECTION_WHY[sectionId];
}

/**
 * Curated section ordering. Sections appear in this order in the
 * sidebar; the order tracks the USCIS form sequence (beneficiary
 * sections first, then sponsor, then meta / signatures).
 *
 * Catalog sections not listed here are appended to the end in
 * `ORDERED_SECTIONS` so a new catalog section never silently
 * disappears — the curator simply hasn't placed it yet.
 */
const CURATED_ORDER: readonly string[] = [
  // Beneficiary core
  'identity',
  'physical_address',
  'biographic',
  'marriage',
  'children',
  'parents',
  // Beneficiary immigration / travel / employment
  'immigration_history',
  'travel_history',
  'travel_document',
  'employment',
  // Beneficiary eligibility
  'eligibility',
  'eligibility_category',
  // Documents
  'documents',
  // Sponsor (I-864) — comes after beneficiary so the application
  // tells the beneficiary's story end-to-end before pivoting to the
  // sponsor's affidavit of support.
  'sponsor_identity',
  'sponsor_physical_address',
  'sponsor_marriage',
  'sponsor_parents',
  'sponsor_household',
  'financials',
  'sponsor_basis_for_filing',
  // Application meta. Interpreter / preparer / safe-address removed
  // 2026-05-26 (not part of this app's scope).
  'petition_type',
  'accommodation',
  'additional_info',
  // Signatures & download last — terminal action of the intake.
  'signatures',
];

export const ORDERED_SECTIONS: readonly string[] = (() => {
  const catalogSet = new Set(SECTION_IDS);
  const ordered: string[] = [];
  const placed = new Set<string>();
  for (const id of CURATED_ORDER) {
    if (catalogSet.has(id)) {
      ordered.push(id);
      placed.add(id);
    }
  }
  // Append any catalog sections not yet placed (forward-compatibility
  // — a new catalog section is rendered at the end rather than
  // silently dropped).
  for (const id of SECTION_IDS) {
    if (!placed.has(id)) {
      ordered.push(id);
    }
  }
  return ordered;
})();

/**
 * Parent-group metadata for the 2-level nav.
 *
 * The catalog sections roll up into 5 parent groups in the
 * collapsible left tree:
 *
 *   1. About you           — beneficiary's own information
 *   2. About your sponsor  — sponsor's information (I-864)
 *   3. Documents           — supporting documents (parallel track)
 *   4. Filing details      — petition meta, preparer, interpreter, etc.
 *   5. Print & mail        — review, print, sign, mail (terminal step)
 *
 * Within "About you" and "About your sponsor", non-collapsible
 * sub-headings (Personal / Family / Background / Eligibility, etc.)
 * break long lists into scannable visual chunks. Sub-headings are
 * NOT nav nodes — they are pure visual groupings rendered inline.
 *
 * This metadata is data-only in Slice 1 — Slice 2 wires it up to the
 * actual collapsible sidebar.
 *
 * Source: docs/rebuild/phase-7ia-plan.md (decisions locked 2026-04-26).
 */
export type TParentGroupId =
  | 'about_you'
  | 'about_sponsor'
  | 'documents'
  | 'filing_details'
  | 'print_and_mail';

/**
 * Human-readable display labels for the 5 parent groups, in render
 * order. The values mirror the headings in `phase-7ia-plan.md`.
 */
export const PARENT_GROUP_DISPLAY_LABELS: Readonly<
  Record<TParentGroupId, string>
> = {
  about_you: 'About you',
  about_sponsor: 'About your sponsor',
  documents: 'Documents',
  filing_details: 'Filing details',
  print_and_mail: 'Submit for review',
};

/**
 * One sub-heading inside a parent group. Sub-headings are visual
 * groupings only — they are not navigable nodes. The `label` is a
 * human-readable header rendered inline in the nav (e.g., "Personal",
 * "Family", "Background"); the `sectionIds` are the catalog sections
 * that appear underneath it in render order.
 */
export interface IParentGroupSubHeading {
  label: string;
  sectionIds: readonly string[];
}

/**
 * Optional summary leaf at the end of a parent
 * group. Selecting the leaf mounts the `ParentReviewPanel` (a "Review
 * your info" / "Review sponsor info" destination) instead of a catalog
 * section.
 *
 * The leaf id uses a reserved `__review_` prefix so it never collides
 * with a real catalog `sectionId` and the section-state nav model stays
 * untouched (no new top-level route). The leaf is OPTIONAL — there is
 * no gate before "Print & mail"; users can ignore it entirely.
 */
export interface IParentGroupSummaryLeaf {
  /**
   * Pseudo-section id used as `currentSectionId` when the user is on
   * the review leaf. Format: `__review_<parent_id>` — the prefix is
   * reserved and is asserted to NOT collide with `SECTION_IDS`.
   */
  id: string;
  /** Label rendered in the tree (e.g., "Review your info"). */
  label: string;
}

/**
 * One parent group in the 2-level nav. Either `subHeadings` (when the
 * parent has visual sub-groupings) or `sectionIds` (when the parent's
 * sections render as a flat list). Today, "About you" and "About your
 * sponsor" use `subHeadings`; the other three parents use a flat
 * `sectionIds` list.
 *
 * `summaryLeaf` (Phase 7-IA Slice 5) appends an optional review-info
 * destination at the end of the parent's nav list. Only the two big
 * parents (`about_you`, `about_sponsor`) carry a leaf — `documents`
 * and `filing_details` are too small to need a per-parent review, and
 * `print_and_mail` is itself the final review destination.
 */
export interface IParentGroup {
  id: TParentGroupId;
  label: string;
  subHeadings?: readonly IParentGroupSubHeading[];
  sectionIds?: readonly string[];
  summaryLeaf?: IParentGroupSummaryLeaf;
}

/**
 * The 5 parent groups in render order, each populated with its
 * sections (or sub-heading-grouped sections). The mapping is the
 * spec from `phase-7ia-plan.md`:
 *
 *   - About you (11 sections, 4 sub-headings)
 *   - About your sponsor (6 sections, 3 sub-headings)
 *   - Documents (1 section, flat)
 *   - Filing details (5 sections, flat)
 *   - Print & mail (1 section, flat)
 *
 * Invariant (asserted by tests): every catalog `sectionId` appears
 * exactly once across all parents — adding a catalog section without
 * placing it here will fail the suite.
 */
export const PARENT_GROUPS: readonly IParentGroup[] = [
  {
    id: 'about_you',
    label: PARENT_GROUP_DISPLAY_LABELS.about_you,
    subHeadings: [
      {
        label: 'Personal',
        sectionIds: [
          'identity',
          'physical_address',
          'biographic',
        ],
      },
      {
        label: 'Family',
        sectionIds: ['marriage', 'children', 'parents'],
      },
      {
        label: 'Background',
        sectionIds: [
          'immigration_history',
          'travel_history',
          'travel_document',
          'employment',
        ],
      },
      {
        label: 'Eligibility',
        sectionIds: ['eligibility', 'eligibility_category'],
      },
    ],
    summaryLeaf: {
      id: '__review_about_you',
      label: 'Review your info',
    },
  },
  {
    id: 'about_sponsor',
    label: PARENT_GROUP_DISPLAY_LABELS.about_sponsor,
    subHeadings: [
      {
        label: 'Personal',
        sectionIds: [
          'sponsor_identity',
          'sponsor_physical_address',
        ],
      },
      {
        label: 'Family',
        sectionIds: [
          'sponsor_marriage',
          'sponsor_parents',
          'sponsor_household',
        ],
      },
      {
        label: 'Financial',
        sectionIds: ['financials', 'sponsor_basis_for_filing'],
      },
    ],
    summaryLeaf: {
      id: '__review_sponsor',
      label: 'Review sponsor info',
    },
  },
  // No summary leaf on `documents`, `filing_details`, or
  // `print_and_mail` (Phase 7-IA Slice 5):
  //   - `documents` and `print_and_mail` are single-section parents;
  //     a per-parent review would just duplicate that one section.
  //   - `filing_details` is the meta tail; it's short, infrequently
  //     consulted, and a review of it doesn't earn its own destination.
  //   - `print_and_mail` is itself the final review/print step.
  {
    id: 'documents',
    label: PARENT_GROUP_DISPLAY_LABELS.documents,
    sectionIds: ['documents'],
  },
  {
    id: 'filing_details',
    label: PARENT_GROUP_DISPLAY_LABELS.filing_details,
    sectionIds: [
      'petition_type',
      'accommodation',
      'additional_info',
    ],
  },
  {
    id: 'print_and_mail',
    label: PARENT_GROUP_DISPLAY_LABELS.print_and_mail,
    sectionIds: ['signatures'],
  },
] as const;

/**
 * Inverse map from a summary-leaf id to the
 * parent group it belongs to. Used by App.tsx to detect when
 * `currentSectionId` is a review destination (vs. a real catalog
 * section) and mount `ParentReviewPanel` instead of `SectionRenderer`.
 *
 * Returns a fresh `Map` per call; the catalog of leaves is small (≤2
 * today) so memoization isn't worth the extra surface area.
 */
export function getSummaryLeafIdToParentId(): Map<string, TParentGroupId> {
  const out = new Map<string, TParentGroupId>();
  for (const group of PARENT_GROUPS) {
    if (group.summaryLeaf) {
      out.set(group.summaryLeaf.id, group.id);
    }
  }
  return out;
}

/**
 * Returns the parent-group id that owns `sectionId`, or `undefined`
 * if the section is not placed in any parent group. Walks both the
 * flat `sectionIds` lists and the per-sub-heading `sectionIds`.
 */
export function getParentGroupForSection(
  sectionId: string,
): TParentGroupId | undefined {
  for (const group of PARENT_GROUPS) {
    if (group.sectionIds && group.sectionIds.includes(sectionId)) {
      return group.id;
    }
    if (group.subHeadings) {
      for (const sub of group.subHeadings) {
        if (sub.sectionIds.includes(sectionId)) {
          return group.id;
        }
      }
    }
  }
  return undefined;
}
